/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo } from 'react';
import { Form, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetCropsQuery } from '../../../../api/cropsApi';
import { useListFieldsQuery } from '../../../../api/fieldsApi';
import type { PlantingFormModalProps, PlantingFormModalValues, PlantingFormModalValuesLocation } from './types';
import { formatDateTime } from './utils';
import type { PlantingMetadata } from './types';

// Hooks
import { useLockedLocation } from './hooks/useLockedLocation';
import { useRotationCheck } from './hooks/useRotationCheck';
import { useLocationCascade } from './hooks/useLocationCascade';
import { useFormInitialization } from './hooks/useFormInitialization';
import { useFormSubmit } from './hooks/useFormSubmit';

// Sections
import MetadataSection from './sections/MetadataSection';
import CropSection from './sections/CropSection';
import BasicFieldsSection from './sections/BasicFieldsSection';
import OriginSection from './sections/OriginSection';
import LockedLocationSection from './sections/LockedLocationSection';
import LocationsListSection from './sections/LocationsListSection';

const PlantingFormModal: React.FC<PlantingFormModalProps> = ({
  open,
  title,
  confirmText,
  loading,
  farmId,
  initialPlanting,
  defaultLocations,
  defaultCropId,
  submitLocations = true,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation('plantings');
  const [form] = Form.useForm<PlantingFormModalValues>();

  // --- Data queries ---
  const cropsQueryArg = open && farmId
    ? { farmId: String(farmId), limit: 200, offset: 0 }
    : skipToken;
  const { data: crops = [], isFetching: cropsLoading } = useGetCropsQuery(cropsQueryArg);

  const fieldsQueryArg = farmId ? farmId : skipToken;
  const { data: fields = [], isFetching: fieldsLoading } = useListFieldsQuery(fieldsQueryArg);

  // --- Hooks ---
  const { lockedLocation, lockedLocationInfo } = useLockedLocation(defaultLocations);
  const { rotationCheck, runRotationCheck, resetRotationCheck } = useRotationCheck();

  const cascade = useLocationCascade({ form, lockedLocation });

  const isEditMode = Boolean(initialPlanting?.id);
  const allowAddLocations = !lockedLocation && !isEditMode;

  // --- Form initialization ---
  const { initialLocationsRef } = useFormInitialization({
    open,
    form,
    initialPlanting,
    defaultLocations,
    defaultCropId,
    lockedLocation,
    onInitialized: useCallback(
      ({ normalizedLocations, cropId, bedId }) => {
        // Trigger rotation check
        if (cropId && bedId) {
          runRotationCheck(cropId, bedId);
        }

        // Prefetch cascade data for existing locations
        const uniqueFieldIds = new Set(
          normalizedLocations
            .map((item: PlantingFormModalValuesLocation) =>
              item.fieldId != null ? String(item.fieldId) : null,
            )
            .filter((v: string | null): v is string => Boolean(v)),
        );
        const uniquePlotIds = new Set(
          normalizedLocations
            .map((item: PlantingFormModalValuesLocation) =>
              item.plotId != null ? String(item.plotId) : null,
            )
            .filter((v: string | null): v is string => Boolean(v)),
        );

        uniqueFieldIds.forEach((fieldId) => cascade.loadPlotsForField(fieldId));
        uniquePlotIds.forEach((plotId) => cascade.ensureBedsForPlot(plotId));
      },
      [runRotationCheck, cascade],
    ),
    onClose: useCallback(() => {
      cascade.setPlotsByField({});
      cascade.setBedsByPlot({});
      resetRotationCheck();
    }, [cascade, resetRotationCheck]),
  });

  // --- Submit ---
  const { handleSubmit } = useFormSubmit({
    form,
    lockedLocation,
    initialLocationsRef,
    submitLocations,
    onSubmit,
  });

  // --- Derived ---
  const metadataInfo = useMemo<PlantingMetadata | null>(() => {
    if (!initialPlanting) return null;
    return {
      plantingId: initialPlanting.publicId ?? initialPlanting.id ?? null,
      createdAt: formatDateTime(initialPlanting.createdAt),
      createdBy: initialPlanting.createdBy ?? null,
      statusChangedAt: formatDateTime(initialPlanting.statusChangedAt),
      statusChangedBy: initialPlanting.statusChangedBy ?? null,
    };
  }, [initialPlanting]);

  const locationsListRules = useMemo(
    () =>
      lockedLocation
        ? []
        : [
            {
              validator: (_: any, value: PlantingFormModalValuesLocation[]) =>
                value && value.length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('form.location.atLeastOne'))),
            },
          ],
    [lockedLocation, t],
  );

  const handleCropChange = useCallback(
    (cropId: string | undefined) => {
      const bedId =
        lockedLocation?.bedId ?? form.getFieldValue(['locations', 0, 'bedId']);
      runRotationCheck(cropId, bedId ? String(bedId) : undefined);
    },
    [lockedLocation, form, runRotationCheck],
  );

  const handleBedChangeForRotation = useCallback(
    (bedId: string | undefined) => {
      const cropId = form.getFieldValue('cropId');
      runRotationCheck(cropId ? String(cropId) : undefined, bedId);
    },
    [form, runRotationCheck],
  );

  // --- Render ---
  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={confirmText ?? t('common:actions.save')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={loading}
      width={720}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" preserve={false}>
        {metadataInfo && <MetadataSection metadata={metadataInfo} />}

        <CropSection
          crops={crops}
          cropsLoading={cropsLoading}
          rotationCheck={rotationCheck}
          onCropChange={handleCropChange}
        />

        <BasicFieldsSection />
        <OriginSection />

        {lockedLocation && lockedLocationInfo ? (
          <LockedLocationSection info={lockedLocationInfo} />
        ) : (
          <LocationsListSection
            form={form}
            fields={fields}
            fieldsLoading={fieldsLoading}
            plotsByField={cascade.plotsByField}
            bedsByPlot={cascade.bedsByPlot}
            plotsLoading={cascade.plotsLoading}
            bedsLoading={cascade.bedsLoading}
            isEditMode={isEditMode}
            allowAddLocations={allowAddLocations}
            locationsListRules={locationsListRules}
            onFieldChange={cascade.handleFieldChange}
            onPlotChange={cascade.handlePlotChange}
            onBedChange={handleBedChangeForRotation}
          />
        )}
      </Form>
    </Modal>
  );
};

export default PlantingFormModal;
